import { createTheme } from '@mui/material';
import { FontsEnum } from 'core/constants/enums/FontsEnum.ts';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';

/*
  FONT WEIGHTS MAPPING
  --------------------
  light     | 300
  regular   | 400
  medium    | 500
  semi-bold | 600
  bold      | 700
 */

const appThemeWithoutCustomStyles = createTheme({
	spacing: 8,
	breakpoints: {
		keys: ['xs', 'mm', 'sm', 'md', 'mobileUi', 'lg', 'xl', 'xxl'],
		values: {
			xs: 0,
			mm: 375,
			sm: 600,
			md: 900,
			mobileUi: 1080,
			lg: 1200,
			xl: 1440,
			xxl: 1700,
		},
		unit: 'px',
	},
	palette: {
		background: {
			default: colorPalette.white,
		},
		text: {
			primary: colorPalette.light4,
		},
		primary: {
			main: '#0D1F39',
		},
		colors: {
			...colorPalette,
		},
	},
	typography: {
		fontFamily: FontsEnum.INTER,
		skHeight: '1em',
		pxToRem: (value) => value / 16,
		scrollMarginTop: 'calc(var(--MuiDocs-header-height) + 32px)',
	},
	components: {
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					// Map the custom variant to render a <h1> by default
					d1_light: 'h1',
					d1_regular: 'h1',
					d1_bold: 'h1',

					d1_v2_light: 'h1',
					d1_v2_regular: 'h1',
					d1_v2_bold: 'h1',

					// Map the custom variant to render a <h2> by default
					d2_light: 'h2',
					d2_regular: 'h2',
					d2_bold: 'h2',

					d2_v2_light: 'h2',
					d2_v2_regular: 'h2',
					d2_v2_bold: 'h2',

					// Map the custom variant to render a <h3> by default
					d3_light: 'h3',
					d3_regular: 'h3',
					d3_bold: 'h3',

					d3_v2_light: 'h3',
					d3_v2_regular: 'h3',
					d3_v2_bold: 'h3',
					d3_v2_medium: 'h3',

					// Map the custom variant to render a <h4> by default
					h1_regular: 'h4',
					h1_medium: 'h4',
					h1_bold: 'h4',

					h1_v2_regular: 'h4',
					h1_v2_medium: 'h4',
					h1_v2_bold: 'h4',

					h1_v3_regular: 'h4',

					h1_zodiak_medium: 'h4',
					// Map the custom variant to render a <h5> by default
					h2_v3_medium: 'h5',
					h2_regular: 'h5',
					h2_medium: 'h5',
					h2_bold: 'h5',

					h2_v2_regular: 'h5',
					h2_v2_medium: 'h5',
					h2_v2_bold: 'h5',

					// Map the custom variant to render a <h6> by default
					t1_regular: 'h6',
					t1_medium: 'h6',
					t1_bold: 'h6',

					t1_v2_regular: 'h6',
					t1_v2_medium: 'h6',
					t1_v2_bold: 'h6',

					t1_v3_medium: 'h6',

					// Map the custom variant to render a <p> by default
					body1: 'p',
					t2_regular: 'p',
					t2_medium: 'p',
					t2_bold: 'p',

					t2_v2_regular: 'p',
					t2_v2_medium: 'p',
					t2_v2_bold: 'p',

					b1_regular: 'p',
					b1_medium: 'p',
					b1_bold: 'p',

					b1_v2_regular: 'p',
					b1_v2_medium: 'p',
					b1_v2_bold: 'p',

					b1_overline_medium: 'p',

					b2_regular: 'p',
					b2_medium: 'p',
					b2_bold: 'p',

					b2_v2_regular: 'p',
					b2_v2_medium: 'p',
					b2_v2_bold: 'p',

					b2_caption: 'p',
					b2_caption_medium: 'p',
					b2_caption_bold: 'p',

					b3_regular: 'p',
					b3_medium: 'p',
					b3_bold: 'p',

					b3_v2_regular: 'p',
					b3_v2_medium: 'p',
					b3_v2_bold: 'p',

					numbers: 'p',

					tooltip_regular: 'p',
					tooltip_medium: 'p',
					tooltip_bold: 'p',

					tooltip_v2_regular: 'p',
					tooltip_v2_medium: 'p',
					tooltip_v2_bold: 'p',

					nd1: 'p',
					nd2: 'p',
					nd3: 'p',
					np1: 'p',
					np2: 'p',
					np3: 'p',
					ns1: 'p',
					ns2: 'p',
					nl1: 'p',
					nl2: 'p',
					nt: 'p',

					input_label: 'p',
					info_text: 'p',
					alert_header: 'p',
				},
			},
		},
	},
});

const appTheme = createTheme(appThemeWithoutCustomStyles, {
	typography: {
		//
		// DESIGN SYSTEM UPDATE
		//

		// D1
		d1_light: {
			fontSize: '96px',
			lineHeight: '112px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 200,
		},
		d1_regular: {
			fontSize: '96px',
			lineHeight: '112px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		d1_bold: {
			fontSize: '96px',
			lineHeight: '112px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 800,
		},
		/* Headings/H1 Large NEW */
		h1_v3_regular: {
			fontSize: '48px',
			lineHeight: '56px' /* 117.333% */,
			letterSpacing: '-0.96px',
			fontFamily: FontsEnum.ZODIAK,
			fontWeight: 540,

			[appThemeWithoutCustomStyles.breakpoints.down('xxl')]: {
				fontSize: '42px',
				lineHeight: '52px' /* 117.333% */,
				letterSpacing: '-0.84px',
				fontFamily: FontsEnum.ZODIAK,
				fontWeight: 540,
			},
			[appThemeWithoutCustomStyles.breakpoints.down('lg')]: {
				fontSize: '36px',
				lineHeight: '44px' /* 117.333% */,
				letterSpacing: '-0.72px',
				fontFamily: FontsEnum.ZODIAK,
				fontWeight: 540,
			},
			[appThemeWithoutCustomStyles.breakpoints.down('sm')]: {
				fontSize: '30px',
				lineHeight: '36px' /* 117.333% */,
				letterSpacing: '-0.6px',
				fontFamily: FontsEnum.ZODIAK,
				fontWeight: 540,
			},
		},

		// D2
		d2_light: {
			fontSize: '56px',
			lineHeight: '64px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 200,
		},
		d2_regular: {
			fontSize: '56px',
			lineHeight: '64px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		d2_bold: {
			fontSize: '56px',
			lineHeight: '64px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 800,
		},

		// D3
		d3_light: {
			fontSize: '48px',
			lineHeight: '56px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 200,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '32px',
			},
		},
		d3_regular: {
			fontSize: '48px',
			lineHeight: '56px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '32px',
			},
		},
		d3_bold: {
			fontSize: '48px',
			lineHeight: '56px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 800,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '32px',
			},
		},
		d3_v2_medium: {
			fontSize: '45px',
			lineHeight: '52px',
			letterSpacing: '-0.45px',
			fontFamily: FontsEnum.ZODIAK,
			fontWeight: 540,
			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '30px',
			},
		},

		// H1
		h1_regular: {
			fontSize: '32px',
			lineHeight: '40px',
			letterSpacing: '0.25px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
		},
		h1_medium: {
			fontSize: '32px',
			lineHeight: '40px',
			letterSpacing: '0.25px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
		},
		h1_bold: {
			fontSize: '32px',
			lineHeight: '40px',
			letterSpacing: '0.25px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
		},

		// H2
		h2_v3_medium: {
			/* Headings/H2 Medium new */
			fontSize: '30px',
			lineHeight: '32px',
			letterSpacing: '-0.3px',
			fontFamily: FontsEnum.ZODIAK,
			fontWeight: 540,
		},
		h2_regular: {
			fontSize: '24px',
			lineHeight: '32px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		h2_medium: {
			fontSize: '24px',
			lineHeight: '32px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		h2_bold: {
			fontSize: '24px',
			lineHeight: '32px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		// T1
		t1_regular: {
			fontSize: '20px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		t1_medium: {
			fontSize: '20px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		t1_bold: {
			fontSize: '20px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},

		// body1
		body1: {
			fontSize: '16px',
			lineHeight: '1.5em', // 24px
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},

		// T2
		t2_regular: {
			fontSize: '16px',
			lineHeight: '1.5em', // 24px
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},
		t2_medium: {
			fontSize: '16px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},
		t2_bold: {
			fontSize: '16px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},

		// B1
		b1_regular: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.1px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		b1_medium: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.1px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		b1_bold: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		/* Overline/Medium */
		b1_overline_medium: {
			fontSize: '12px',
			lineHeight: '16px' /* 133.333% */,
			fontFamily: FontsEnum.INTER,
			textAlign: 'center',
			letterSpacing: '2.4px',
			textTransform: 'uppercase',
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('lg')]: {
				fontSize: '10.5px',
			},
		},

		// B2
		b2_regular: {
			fontFamily: FontsEnum.INTER,
			fontSize: '12px',
			fontWeight: 400,
			lineHeight: '14px',
			textAlign: 'left',
		},
		b2_medium: {
			fontFamily: FontsEnum.INTER,
			fontSize: '12px',
			fontWeight: 500,
			lineHeight: '14px',
			textAlign: 'left',
		},
		b2_bold: {
			fontFamily: FontsEnum.INTER,
			fontSize: '12px',
			fontWeight: 600,
			lineHeight: '14px',
			textAlign: 'left',
		},

		b2_caption: {
			fontSize: '12px',
			lineHeight: '14px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		b2_caption_medium: {
			fontSize: '12px',
			lineHeight: '14px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		b2_caption_bold: {
			fontSize: '12px',
			lineHeight: '14px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		// B3
		b3_regular: {
			fontFamily: FontsEnum.INTER,
			fontSize: '11px',
			fontWeight: 400,
			lineHeight: '14px',
			textAlign: 'left',
		},
		b3_medium: {
			fontFamily: FontsEnum.INTER,
			fontSize: '11px',
			fontWeight: 500,
			lineHeight: '14px',
			textAlign: 'left',
		},
		b3_bold: {
			fontFamily: FontsEnum.INTER,
			fontSize: '11px',
			fontWeight: 600,
			lineHeight: '14px',
			textAlign: 'left',
		},

		numbers: {
			fontSize: '12px',
			lineHeight: '16px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},

		// tooltip
		tooltip_regular: {
			fontSize: '10px',
			lineHeight: '14px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		tooltip_medium: {
			fontSize: '10px',
			lineHeight: '14px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		tooltip_bold: {
			fontSize: '10px',
			lineHeight: '14px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		// NUMBERS
		nd1: {
			fontSize: '40px',
			lineHeight: '52.75px',
			letterSpacing: '0.17px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 700,
		},
		nd2: {
			fontSize: '32px',
			lineHeight: '42.2px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},
		nd3: {
			fontSize: '16px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		np1: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.17px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 700,
		},
		np2: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.17px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		np3: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.17px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		ns1: {
			fontSize: '12px',
			lineHeight: '16px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		ns2: {
			fontSize: '12px',
			lineHeight: '16px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		nl1: {
			fontSize: '10px',
			lineHeight: '16px',
			letterSpacing: '-0.2px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		nl2: {
			fontSize: '10px',
			lineHeight: '16px',
			letterSpacing: '-0.2px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		nt: {
			fontSize: '8px',
			lineHeight: '12px',
			letterSpacing: '-0.2px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},

		button: {
			fontSize: '14px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		input_label: {
			fontSize: '12px',
			fontWeight: 500,
			lineHeight: '24px',
			letterSpacing: '0.1px',
			fontFamily: FontsEnum.INTER,
		},
		info_text: {
			fontSize: '12px',
			fontWeight: 400,
			lineHeight: '24px',
			fontFamily: FontsEnum.INTER,
		},
		alert_header: {
			fontSize: '16px',
			fontWeight: 700,
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
		},

		// DESIGN SYSTEM UPDATE V2
		//

		// D1
		d1_v2_light: {
			fontSize: '96px',
			lineHeight: '96px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 200,
		},
		d1_v2_regular: {
			fontSize: '96px',
			lineHeight: '96px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		d1_v2_bold: {
			fontSize: '96px',
			lineHeight: '96px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 800,
		},

		// D2
		d2_v2_light: {
			fontSize: '56px',
			lineHeight: '56px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 200,
		},
		d2_v2_regular: {
			fontSize: '56px',
			lineHeight: '56px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		d2_v2_bold: {
			fontSize: '56px',
			lineHeight: '56px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 800,
		},

		// D3
		d3_v2_light: {
			fontSize: '48px',
			lineHeight: '48px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 200,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '32px',
			},
		},
		d3_v2_regular: {
			fontSize: '48px',
			lineHeight: '48px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '32px',
			},
		},
		d3_v2_bold: {
			fontSize: '48px',
			lineHeight: '48px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 800,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '32px',
			},
		},

		// H1
		h1_v2_regular: {
			fontSize: '32px',
			lineHeight: '32px',
			letterSpacing: '0.25px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
		},
		h1_v2_medium: {
			fontSize: '32px',
			lineHeight: '32px',
			letterSpacing: '0.25px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
		},
		h1_v2_bold: {
			fontSize: '32px',
			lineHeight: '32px',
			letterSpacing: '0.25px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
		},
		h1_zodiak_medium: {
			fontSize: '36px',
			lineHeight: '44px',
			letterSpacing: '-1.08px',
			fontFamily: FontsEnum.ZODIAK,
			fontWeight: 540,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '24px',
			},
			[appThemeWithoutCustomStyles.breakpoints.down('sm')]: {
				fontSize: '20px',
			},
		},
		// H2
		h2_v2_regular: {
			fontSize: '24px',
			lineHeight: '24px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		h2_v2_medium: {
			fontSize: '24px',
			lineHeight: '24px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		h2_v2_bold: {
			fontSize: '24px',
			lineHeight: '24px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		// T1
		t1_v2_regular: {
			fontSize: '20px',
			lineHeight: '20px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		t1_v2_medium: {
			fontSize: '20px',
			lineHeight: '20px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		t1_v3_medium: {
			fontSize: '21px',
			lineHeight: '24px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},
		t1_v2_bold: {
			fontSize: '20px',
			lineHeight: '20px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '16px',
			},
		},

		// T2
		t2_v2_regular: {
			fontSize: '16px',
			lineHeight: '16px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},
		t2_v2_medium: {
			fontSize: '16px',
			lineHeight: '16px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},
		t2_v2_bold: {
			fontSize: '16px',
			lineHeight: '16px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,

			[appThemeWithoutCustomStyles.breakpoints.down('md')]: {
				fontSize: '14px',
			},
		},

		// B1
		b1_v2_regular: {
			fontSize: '14px',
			lineHeight: '14px',
			letterSpacing: '0.1px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		b1_v2_medium: {
			fontSize: '14px',
			lineHeight: '14px',
			letterSpacing: '0.1px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		b1_v2_bold: {
			fontSize: '14px',
			lineHeight: '14px',
			letterSpacing: '0.15px',
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		// B2
		b2_v2_regular: {
			fontFamily: FontsEnum.INTER,
			fontSize: '12px',
			fontWeight: 400,
			lineHeight: '12px',
			textAlign: 'left',
		},
		b2_v2_medium: {
			fontFamily: FontsEnum.INTER,
			fontSize: '12px',
			fontWeight: 500,
			lineHeight: '12px',
			textAlign: 'left',
		},
		b2_v2_bold: {
			fontFamily: FontsEnum.INTER,
			fontSize: '12px',
			fontWeight: 600,
			lineHeight: '12px',
			textAlign: 'left',
		},

		b3_v2_regular: {
			fontFamily: FontsEnum.INTER,
			fontSize: '11px',
			fontWeight: 400,
			lineHeight: '11px',
			textAlign: 'left',
		},
		b3_v2_medium: {
			fontFamily: FontsEnum.INTER,
			fontSize: '11px',
			fontWeight: 500,
			lineHeight: '11px',
			textAlign: 'left',
		},
		b3_v2_bold: {
			fontFamily: FontsEnum.INTER,
			fontSize: '11px',
			fontWeight: 600,
			lineHeight: '11px',
			textAlign: 'left',
		},

		// tooltip
		tooltip_v2_regular: {
			fontSize: '10px',
			lineHeight: '10px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 400,
		},
		tooltip_v2_medium: {
			fontSize: '10px',
			lineHeight: '10px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 500,
		},
		tooltip_v2_bold: {
			fontSize: '10px',
			lineHeight: '10px',
			letterSpacing: 0,
			fontFamily: FontsEnum.INTER,
			fontWeight: 600,
		},

		tooltip_v3_regular: {
			fontSize: '10.5px',
			fontWeight: 400,
			lineHeight: '16px',
			fontFamily: FontsEnum.INTER,
			letterSpacing: '0.105px',
		},

		//
		// DESIGN SYSTEM UPDATE END
		//
	},
});

export default appTheme;
